import moment from 'moment';
import { BorrowerModel } from '../../cmg-1003/models';
import { CreditSummaryModel } from '../../cmg-1003/models/credit-summary.model';
import { LoanItemStatus, LoanItemStatusIconType } from '../../shared/models';
import { ILoanStatusCard, LoanStatusCardConfig, LoanStatusCardType } from './loan-status-card.model';

export const GetCreditStatus = (creditSummary: CreditSummaryModel, borrowers: BorrowerModel[]): ILoanStatusCard => {
  const cardBase = LoanStatusCardConfig.find((c) => c.cardType === LoanStatusCardType.Credit);
  const card = <ILoanStatusCard>{ ...cardBase, cardType: LoanStatusCardType.Credit };
  const expirationDate = moment(creditSummary?.lastSuccessfulRunDate).add(120, 'days');
  const daysToExpiration = expirationDate.diff(moment.now(), 'days');

  const lowestMedianCreditScoreOfAllBorrowers = borrowers?.reduce(
    (prev, curr) =>
      (prev?.creditScores?.creditScoreMedian ?? Number.MAX_VALUE) < (curr?.creditScores?.creditScoreMedian ?? Number.MAX_VALUE) ? prev : curr,
    null,
  )?.creditScores?.creditScoreMedian;

  const hasMissingReason = borrowers?.some(
    (b) =>
      !(b?.creditScores?.equifaxScore || b?.creditScores?.experianScore || b?.creditScores?.transUnionScore) &&
      (b?.creditScores?.equifaxMissingScoreReason || b?.creditScores?.experianMissingScoreReason || b?.creditScores?.transUnionMissingScoreReason),
  );

  // No Credit Run for All Apps:
  if (!creditSummary?.applicationsWithCreditRun || (!lowestMedianCreditScoreOfAllBorrowers && !hasMissingReason)) {
    card.status = LoanItemStatus.NotComplete;
    card.icon = LoanItemStatusIconType.EmptyCircle;
    card.descriptionHtml = `Not Complete`;
  }

  // Credit Run on Some Apps:
  if (
    creditSummary?.applicationsWithCreditRun < creditSummary?.totalNumberOfApplications &&
    (!!lowestMedianCreditScoreOfAllBorrowers || hasMissingReason)
  ) {
    card.status = LoanItemStatus.NotComplete;
    card.icon = LoanItemStatusIconType.EmptyCircle;
    card.descriptionHtml = `<b>${creditSummary?.applicationsWithCreditRun}</b> of <b>${creditSummary?.totalNumberOfApplications}</b> apps run`;
  }

  // Credit Run:
  if (
    creditSummary?.applicationsWithCreditRun >= creditSummary?.totalNumberOfApplications &&
    (!!lowestMedianCreditScoreOfAllBorrowers || hasMissingReason)
  ) {
    if (hasMissingReason) {
      card.status = LoanItemStatus.Complete;
      card.icon = LoanItemStatusIconType.Circle;
      card.descriptionHtml = 'No Scores Available';
    } else if (daysToExpiration > 5) {
      card.status = LoanItemStatus.Complete;
      card.icon = LoanItemStatusIconType.Circle;
      card.descriptionHtml = lowestMedianCreditScoreOfAllBorrowers
        ? `Rep. Score <b>${lowestMedianCreditScoreOfAllBorrowers}</b>`
        : 'No Scores Available';
    } else if (daysToExpiration > 0 && daysToExpiration <= 5) {
      card.status = LoanItemStatus.Warning;
      card.icon = LoanItemStatusIconType.Diamond;
      card.descriptionHtml = `Exp. in <b>${daysToExpiration}</b> days`;
    } else {
      card.status = LoanItemStatus.Error;
      card.icon = LoanItemStatusIconType.Square;
      card.descriptionHtml = `Expired <b>${expirationDate.format('M/D/YY')}</b>`;
    }
  }

  return card;
};
